import { Builder } from '@builder.io/react';
import { BUILDER_INTERACTIVE_SECTION_COMPONENTS_NAMES } from '../BuilderComponentsName';
import { S20ChoicesSection } from '../components/interactive-sections/S20Choices';
import {
  backgroundGradientOptions,
  textColorOptionsLimited,
  iconColorOptions,
  buttonVariantOptions
} from '../utils/builderColorOptions';
import { getBackgroundColorInput, getBackgroundGradientInput, getPaddingInputs } from '../utils/builderSharedInputs';

Builder.registerComponent(S20ChoicesSection, {
  name: BUILDER_INTERACTIVE_SECTION_COMPONENTS_NAMES.S20Choices,
  friendlyName: 'S20 Choices',
  inputs: [
    {
      name: 'headline',
      type: 'string',
      friendlyName: 'Headline',
      helperText: 'Optional main headline displayed at the top of the section'
    },
    {
      name: 'headlineColor',
      type: 'string',
      friendlyName: 'Headline Color',
      enum: textColorOptionsLimited,
      defaultValue: 'Blue',
      helperText: 'Select a color for the headline text'
    },
    {
      name: 'textColor',
      type: 'string',
      friendlyName: 'Text Color',
      enum: textColorOptionsLimited,
      defaultValue: 'Navy',
      helperText: 'Select a color for the main text content'
    },
    {
      name: 'iconColor',
      type: 'string',
      friendlyName: 'Icon Color',
      enum: iconColorOptions,
      defaultValue: 'Blue',
      helperText: 'Select a color for the card icons'
    },
    {
      name: 'cardHeadlineColor',
      type: 'string',
      friendlyName: 'Card Headline Color',
      enum: textColorOptionsLimited,
      defaultValue: 'Blue',
      helperText: 'Select a color for the card headline text'
    },
    {
      name: 'cards',
      friendlyName: 'Choice Cards',
      type: 'list',
      required: true,
      defaultValue: [
        {
          headline: 'Shaving',
          text: 'A razor cuts the hair directly at the surface. The hair regrowth is real and happens in just a day because the root is still intact. Daily shaving is time-consuming and can cause ingrown hairs, nicks, strawberry skin, and inflamed hair follicles. Spending money on razors, shaving cream, and exfoliants adds up over time, making it more expensive than it seems.',
          icon: 'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2Fe5b06b4c45114257bcd4f3625476733a',
          image: 'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2Ffeef07b1c18f4e6ea348efd57998990e',
          imageAltText: 'Shaving image',
          button: {
            label: 'Learn More',
            variant: 'primary',
            url: '/blog/hair-removal-methods',
          }
        },
        {
          headline: 'Tweezing',
          text: 'Many choose to tweeze or pluck facial hair. Tweezing removes individual hairs by pulling them from the root with tweezers. The hair is removed from the root, so the regrowth can take longer than shaving. Tweezing can be time-consuming, and repeated plucking can cause skin irritation and ingrown hairs. ',
          icon: 'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2Fcebd109f49aa4f90958df6fe1d84ca31',
          image: 'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2F7a983caeee17438f8fd640bc012be672',
          imageAltText: 'Tweezing image',
          button: {
            label: 'Learn More',
            variant: 'primary',
            url: '/blog/laser-hair-removal-vs-tweezing',
          }
        },

        {
          headline: 'Waxing',
          text: 'Hot wax sticks to the hair and is ripped off with a strip. It’s painful and messy, but leaves smooth, hair-free skin. Waxing is considered very painful, especially in intimate areas, and causes ingrown hairs and inflamed follicles. It lasts longer than shaving, but is much more expensive. At least one appointment per month is required to maintain results.',
          icon: 'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2F0fdfd7afd2c144d7a05b1ee417bb015f',
          image: 'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2Ffeef07b1c18f4e6ea348efd57998990e',
          imageAltText: 'Waxing image',
          button: {
            label: 'Learn More',
            variant: 'primary',
            url: '/blog/laser-hair-removal-vs-waxing',

          }
        },
        {
          headline: 'Hair Removal Cream',
          text: 'Depilatory cream, is applied to the skin for 3 to 5 minutes. The chemicals break down the hairs below the skin’s surface. When wiped away, it leaves temporarily smooth skin behind. It’s convenient and easy, but not a long-term solution. Hair will continue to grow because the follicle is still intact, and the process has to be repeated.',
          icon: 'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2Fa22b8f178c29464cac0fd90f1b9ea7d0',
          image: 'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2F54b993bfa90448a7b6220f6b5ef6a815',
          imageAltText: 'Hair Removal Cream image',
          button: {
            label: 'Learn More',
            variant: 'primary',
            url: '/blog/laser-hair-removal-vs-hair-removal-cream',
          }
        },
        {
          headline: 'Electrolysis',
          text: 'A probe is inserted into the hair follicle and delivers an electric current to destroy the root. Laser Electrolysis can work on all hair colors, including gray and white. The process is time-consuming and painful, sometimes leading to scabbing, infections, and skin irritation. Multiple treatments are needed, and it can take years to see full results.',
          icon: 'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2F6a9e66784af642a58798fd1e84047e47',
          image: 'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2Fc3334621c5ec44808f95fb0088932c90',
          imageAltText: 'Electrolysis image',
          button: {
            label: 'Learn More',
            variant: 'primary',
            url: '/blog/laser-hair-removal-vs-electrolysis',
          }
        },
        {
          headline: 'At-Home Devices',
          text: 'Most home devices use intense pulse light (IPL) instead of true laser technology, so they’re not as powerful or effective. Results, if any, can be patchy and inconsistent. There’s no safety oversight—you’re relying on instructions and often with no protective eyewear. At-home laser devices don’t work on all skin tones or facial hair and can cause burns. ',
          icon: 'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2F9a064895bad947648638732d5c7a9149',
          image: 'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2F7612fd9a02c5447391880d2dda163cab',
          imageAltText: 'At-Home Devices image',
          button: {
            label: 'Learn More',
            variant: 'primary',
            url: '/blog/at-home-laser-hair-removal',
          }
        }
      ],
      helperText: 'Add choice cards with content and buttons',
      subFields: [
        {
          name: 'headline',
          type: 'string',
          friendlyName: 'Card Headline',
          required: true,
          helperText: 'Enter the headline for this choice card'
        },
        {
          name: 'text',
          type: 'string',
          friendlyName: 'Card Text',
          required: true,
          helperText: 'Enter the description text for this choice card'
        },
        {
          name: 'icon',
          type: 'file',
          friendlyName: 'Card Icon',
          required: true,
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg', 'webp'],
          helperText: 'Upload an icon for this choice card'
        },
        {
          name: 'image',
          type: 'file',
          friendlyName: 'Card Image',
          required: true,
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg', 'webp'],
          helperText: 'Upload the main image for this choice card'
        },
        {
          name: 'imageAltText',
          type: 'string',
          friendlyName: 'Image Alt Text',
          required: true,
          helperText: 'Alt text for the card image'
        },
        {
          name: 'button',
          type: 'object',
          friendlyName: 'Card Button',
          required: true,
          helperText: 'Configure the button for this choice card',
          subFields: [
            {
              name: 'label',
              type: 'string',
              required: true,
              friendlyName: 'Button Text',
              defaultValue: 'Choose Option',
              helperText: 'The text that will appear on the button'
            },
            {
              name: 'variant',
              type: 'string',
              required: true,
              friendlyName: 'Button Variant',
              enum: buttonVariantOptions,
              defaultValue: 'primary',
              helperText: 'The style of the button: primary (blue), secondary (navy blue), cta (special emphasis)'
            },
            {
              name: 'url',
              type: 'string',
              required: true,
              friendlyName: 'Button URL',
              helperText: 'The destination page when the button is clicked (e.g., /services, /contact, /special-offer)',
              defaultValue: '/process'
            }
          ]
        }
      ]
    },
    ...getBackgroundColorInput({}),
    ...getBackgroundGradientInput({
      backgroundGradientName: 'backgroundGradient',
      backgroundGradientList: backgroundGradientOptions,
      defaultValue: 'BlueTop',
    }),
    {
      name: 'backgroundImage',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'gif', 'svg'],
      friendlyName: 'Background Image',
      helperText: 'Upload a background image or provide an image URL (optional)',
    },
    ...getPaddingInputs({
      desktopTop: 'large',
      desktopBottom: 'large',
      mobileTop: 'large',
      mobileBottom: 'small',
    }),
  ],
});
